import { IAlbum } from '@/data/models/album';
import { ICmsAlbum } from '@/data/models/cms';
import useAlbumId from '@/hooks/useAlbumId';
import useGetAlbumsQuery from '@/hooks/useGetAlbumsQuery';
import useGetCmsAlbumsQuery from '@/hooks/useGetCmsAlbumsQuery';
import { isNotNullOrUndefined } from '@/utils/type-guards';
import { useMemo } from 'react';
import useRelease from './useRelease';

export type GroupedProduct =
  | ({ groupedProductType: 'PRS' } & IAlbum)
  | ({ groupedProductType: 'CMS' } & ICmsAlbum);

export default function useGroupedProducts(): GroupedProduct[] {
  const physicalGroupedProductIds = useGroupedProductAlbumIds('prs');
  const digitalGroupedProductIds = useGroupedProductAlbumIds('cms');

  const { data: prsAlbums } = useGetAlbumsQuery(
    physicalGroupedProductIds ?? []
  );

  const { data: cmsAlbums } = useGetCmsAlbumsQuery(
    digitalGroupedProductIds ?? []
  );

  return [
    ...(prsAlbums ?? []).map((product) => ({
      ...product,
      groupedProductType: 'PRS' as const,
    })),
    ...(cmsAlbums ?? []).map((product) => ({
      ...product,
      groupedProductType: 'CMS' as const,
    })),
  ];
}

/**
 * Hook to retrieve grouped album ID's by system type (PRS or CMS).
 * - Related products are grouped together via a Release in the Project Service.
 */
function useGroupedProductAlbumIds(system: 'prs' | 'cms') {
  const release = useRelease();
  const albumId = useAlbumId();

  return useMemo(() => {
    const regex = new RegExp(`^urn:${system}:([a-zA-Z0-9]+)$`);
    return (
      release?.content
        .map((content) => content.urn.match(regex)?.[1])
        .filter((id) => id && id !== albumId.toString())
        .filter(isNotNullOrUndefined) ?? []
    );
  }, [release, albumId, system]);
}
